import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";

export default function FaqPage() {
  const [open, setOpen] = useState<number | null>(0);

  useSeo({
    title: "常见问题｜灵灵黑料回应、声明与辟谣问答",
    description:
      "关于灵灵黑料争议的常见问题解答：哪些信息已被证实、回应了几次、聊天记录是否可信、粉丝与路人为何分歧，以及本站的内容立场与判断方法。",
    keywords: "灵灵黑料常见问题,灵灵回应几次,聊天记录真假,辟谣分析,信息分级",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页" },
          { "@type": "ListItem", position: 2, name: "常见问题" },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "常见问题" }]} />
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            关于灵灵事件，最常被问到的六个问题
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里集中回答读者最关心的问题，答案与站内正文保持一致。我们尽量只讲可以核验的部分，并把「目前无法确认」的地方明确说出来。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <ul className="space-y-3">
              {faqs.map((item, i) => {
                const isOpen = open === i;
                return (
                  <li
                    key={item.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                  >
                    <h2>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? null : i)}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-4 text-left md:px-6 md:py-5"
                      >
                        <span className="flex items-start gap-3">
                          <span className="mt-0.5 font-heading text-sm font-bold text-primary-500">
                            Q{i + 1}
                          </span>
                          <span className="text-sm font-semibold text-foreground-900 md:text-base">
                            {item.q}
                          </span>
                        </span>
                        <i
                          className={
                            isOpen
                              ? "ri-subtract-line shrink-0 text-lg text-primary-500"
                              : "ri-add-line shrink-0 text-lg text-foreground-400"
                          }
                          aria-hidden="true"
                        ></i>
                      </button>
                    </h2>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <p className="border-t border-background-100 px-4 py-4 text-sm leading-relaxed text-foreground-600 md:px-6 md:py-5">
                        {item.a}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <aside className="lg:col-span-4">
            <div className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-secondary-600" aria-hidden="true"></i>
                  判断信息的四个动作
                </h2>
                <ol className="mt-4 space-y-3">
                  {[
                    "先问这条信息属于事实、观点还是传闻",
                    "追溯第一发布者与发布时间",
                    "换一个独立来源做交叉核对",
                    "无法判断时，保留判断而不是替代它",
                  ].map((t, i) => (
                    <li key={t} className="flex items-start gap-3 text-xs leading-relaxed text-foreground-700">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[10px] font-bold text-secondary-800">
                        {i + 1}
                      </span>
                      {t}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-accent-300 bg-accent-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">问题没有答案？</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                  如果你关心的疑点不在上面，可以来信，我们会补充进问答页或时间线。
                </p>
                <Link
                  to="/about"
                  className="mt-3 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  去提问
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}